import React, { useEffect, useRef } from "react";
import anime from "animejs";
import TableContent from "./TableContent";
import audioPath from "../assets/audio/wheel.mp3";
import image from "../assets/Table.png";

/**
 * Owns the wheel/ball animation. The animation is purely cosmetic: it is
 * started from the winning number the server has already committed to the
 * round, so what it lands on can never disagree with the settled result.
 *
 * The source spun off a `randomWinnerNumber` socket event at countdown tick
 * -2; here the trigger is the round entering 'spinning'.
 */
export default function GameTable(props) {
  const {
    status,
    round,
    wheelNumbers,
    muteSound,
    ...rest
  } = props;

  const lastSpunRoundRef = useRef(null);
  const lastNumberRef = useRef(0);

  const totalNumbers = wheelNumbers.length || 37;
  const singleSpinDuration = 5000;
  const singleRotationDegree = 360 / totalNumbers;

  useEffect(() => {
    if (status !== "spinning" || !round) return;
    if (round.winningNumber == null) return;
    // Guard against re-running on unrelated re-renders within one round.
    if (lastSpunRoundRef.current === round._id) return;
    lastSpunRoundRef.current = round._id;

    const getRouletteIndexFromNumber = (n) => wheelNumbers.indexOf(parseInt(n, 10));
    const getRotationFromNumber = (n) => singleRotationDegree * getRouletteIndexFromNumber(n);
    const getRandomEndRotation = (minSpins, maxSpins) =>
      singleRotationDegree * anime.random(minSpins * totalNumbers, maxSpins * totalNumbers);
    const getZeroEndRotation = (totalRotation) => 360 - Math.abs(totalRotation % 360);
    const getBallEndRotation = (zeroEndRotation, currentNumber) =>
      Math.abs(zeroEndRotation) + getRotationFromNumber(currentNumber);
    const getBallNumberOfRotations = (minSpins, maxSpins) => 360 * anime.random(minSpins, maxSpins);

    const playSound = async () => {
      try {
        const audio = new Audio(audioPath);
        audio.volume = muteSound ? 0 : 1;
        await audio.play();
      } catch (error) {
        // Autoplay can be blocked until the user interacts; not fatal.
      }
    };

    const spinWheel = async (number) => {
      await playSound();
      const bezier = [0.165, 0.84, 0.44, 1.005];
      const lastNumberRotation = getRotationFromNumber(String(lastNumberRef.current));
      const endRotation = -getRandomEndRotation(2, 4);
      const zeroFromEndRotation = getZeroEndRotation(endRotation);
      const ballEndRotation =
        getBallNumberOfRotations(2, 4) + getBallEndRotation(zeroFromEndRotation, number);

      anime.set([".layer-2", ".layer-4"], { rotate: () => lastNumberRotation });
      anime.set(".ball-container", { rotate: () => 0 });

      anime({
        targets: [".layer-2", ".layer-4"],
        rotate: () => endRotation,
        duration: singleSpinDuration,
        easing: `cubicBezier(${bezier.join(",")})`,
        complete: () => { lastNumberRef.current = number; },
      });

      const screenWidth =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth;

      const translateYValues = screenWidth <= 576
        ? [
            { value: 29, duration: 2000 },
            { value: 30, duration: 1000 },
            { value: 40, duration: 900 },
            { value: 50, duration: 1000 },
          ]
        : [
            { value: 0, duration: 2000 },
            { value: 20, duration: 1000 },
            { value: 25, duration: 900 },
            { value: 50, duration: 1000 },
          ];

      anime({
        targets: ".ball-container",
        translateY: translateYValues,
        rotate: [{ value: ballEndRotation, duration: singleSpinDuration }],
        loop: 1,
        easing: `cubicBezier(${bezier.join(",")})`,
      });
    };

    spinWheel(parseInt(round.winningNumber, 10));
  }, [status, round, wheelNumbers, muteSound, singleRotationDegree, totalNumbers]);

  return (
    <div className="game-main" style={{ backgroundImage: `url(${image})` }}>
      <TableContent status={status} round={round} muteSound={muteSound} {...rest} />
    </div>
  );
}
